import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { UserIFC } from "@myProfile/models";

/**
 * Fetches the caller's InvestorFundContact records.
 *
 * API: GET contacts/v1/user/IFCs[?entitledUserId={id}]
 *
 * Server derives identity from the caller's JWT (and entitledUserId for impersonation).
 * Returns:
 *   - Success: UserIFC[] (user has IFCs)
 *   - 404: user has none — handled here as empty array
 *
 * Used to determine profile menu visibility:
 *   - Users with IFCs → can see My Profile
 *   - Users without IFCs → cannot see My Profile or Manage Contacts
 */
export const fetchUserIFCsThunk = createAsyncThunk<UserIFC[], void, ThunkApiConfig>(
  "myProfile/fetchUserIFCs",
  async (_, { extra, rejectWithValue }) => {
    try {
      const response = await extra<UserIFC[]>("contacts/v1/user/IFCs", { method: "GET" });

      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || "Failed to fetch user IFCs";

      // 404 from contact-service when the caller has no IFCs is normal — treat as empty.
      if (error?.response?.status === 404 || errorMessage.includes("No IFC data found")) {
        return [];
      }

      console.error("[fetchUserIFCs] Error fetching user IFCs:", error);

      return rejectWithValue(errorMessage);
    }
  }
);
